import type { ReactNode } from 'react';
import { motion } from 'framer-motion';

/**
 * Button — primary (white pill / black text) or secondary (transparent, white
 * stroke). Signature is additive-only: `tone` layers a ceremonial rim glow onto
 * either variant without changing existing call sites (default = no glow, exact
 * prior look). A6 restyles the visuals only.
 */
export interface ButtonProps {
  variant: 'primary' | 'secondary';
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /**
   * Optional ceremonial accent — adds a soft rim glow (gold or blood) around the
   * button for the dramatic moments. Omit for the default neutral look.
   */
  tone?: 'gold' | 'blood';
  /** Focus on mount (keyboard/gamepad affordance — e.g. END's "Play again"). */
  autoFocus?: boolean;
}

/** Rim-glow box-shadow per tone (layered under the base drop shadow). */
const TONE_GLOW: Record<NonNullable<ButtonProps['tone']>, string> = {
  gold: 'var(--rim-gold, 0 0 24px rgba(230,184,102,0.5))',
  blood: 'var(--rim-blood, 0 0 24px rgba(153,0,0,0.55))',
};

export function Button({ variant, children, onClick, disabled, tone, autoFocus }: ButtonProps) {
  const base: React.CSSProperties = {
    fontFamily: 'var(--font-ui)',
    fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
    fontSize: 'var(--text-md)',
    borderRadius: 'var(--radius-pill)',
    minHeight: 'var(--btn-min-height)',
    paddingLeft: 'var(--space-5)',
    paddingRight: 'var(--space-5)',
    paddingTop: 'var(--space-3)',
    paddingBottom: 'var(--space-3)',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-2)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    border: '1px solid transparent',
    outline: 'none',
    transition: 'opacity var(--duration-fast) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard), filter var(--duration-fast) var(--ease-standard)',
    letterSpacing: '0.01em',
  };

  const baseShadow = '0 2px 8px rgba(0,0,0,0.4)';
  const glow = tone && !disabled ? TONE_GLOW[tone] : '';

  const skin: React.CSSProperties =
    variant === 'primary'
      ? {
          background: 'var(--btn-primary-bg)',
          color: 'var(--btn-primary-text)',
          boxShadow: glow ? `${baseShadow}, ${glow}` : baseShadow,
        }
      : {
          background: 'var(--btn-secondary-bg)',
          color: 'var(--btn-secondary-text)',
          borderColor: tone ? `var(--${tone === 'gold' ? 'gold-4' : 'blood-4'})` : 'var(--btn-secondary-stroke)',
          boxShadow: glow || 'none',
        };

  return (
    <motion.button
      type="button"
      style={{ ...base, ...skin }}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      autoFocus={autoFocus}
      // Hover must be legible without a pointer-shaped cursor (spearhead cursor
      // never changes over buttons) — a scale lift reads on any fill color.
      whileHover={disabled ? undefined : { scale: 1.03 }}
      whileTap={disabled ? undefined : { scale: 0.96 }}
      // The tap settle rides the dramatic ceremonial easing for a weightier press.
      transition={{ duration: 0.12, ease: [0.7, 0, 0.16, 1] }}
    >
      {children}
    </motion.button>
  );
}
